import { useEffect, useRef, useState, type ReactNode } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

/**
 * The textured "Space" model, in the browser.
 *
 * Where `ScanViewer` builds flat coloured boxes from the RoomPlan geometry, this
 * shows the photogrammetry model the Space pipeline produces — a mesh of the
 * actual room carrying the photographs as its texture.
 *
 * WHAT IT LOADS. A GLB (binary glTF), fetched by the caller through the
 * authenticated API client and handed over as bytes (`loadModel`). Not a URL
 * handed to three's loader: every `/api` route needs an `Authorization:
 * Bearer` header and three's loaders cannot send one — the first version of
 * this file did exactly that and could never have loaded a real model. The
 * GLB is unlit (`KHR_materials_unlit`) and single-sided: the texture already
 * IS the room's lighting, and culling back faces is what lets an orbiting
 * camera see into the room through its near walls — the dollhouse view.
 *
 * CONTROLS, hand-rolled like `ScanViewer`'s so the two views feel the same:
 * drag to orbit, right-drag (or shift-drag, or the Pan tool) to pan, wheel to
 * zoom. `fitSignal` reframes the model keeping the current angle;
 * `resetSignal` returns to the starting angle as well.
 *
 * WebGL is guarded exactly as `ScanViewer` guards it: absent in jsdom, off in
 * some locked-down browsers, and its constructor throws rather than returning
 * null.
 */

/** A flattened description of a top-level scene node, for the layer tree. */
export interface SpaceSceneNode {
  readonly uuid: string;
  readonly name: string;
  readonly triangles: number;
}

/** Screen-space directions of the world X/Y/Z axes, for a live gizmo. */
export interface SpaceAxesOrientation {
  readonly x: readonly [number, number, number];
  readonly y: readonly [number, number, number];
  readonly z: readonly [number, number, number];
}

/** The tools the workspace toolbar can activate. */
export type SpaceTool = "select" | "pan" | "measure";

export interface SpaceViewerProps {
  /** Identifies the model; a change reloads it. */
  readonly modelKey: string;
  /** Fetches the GLB bytes (through the authenticated client). */
  readonly loadModel: () => Promise<ArrayBuffer>;
  /** Uuids the workspace has hidden. Toggled onto `object.visible`. */
  readonly hiddenUuids?: ReadonlySet<string>;
  /** The uuid the workspace has selected. Drawn with an outlined bounding box. */
  readonly selectedUuid?: string | null;
  /** Fired on a click over a mesh (select tool) or a click on nothing. */
  readonly onSelectMesh?: (uuid: string | null) => void;
  /** Fired once the model is loaded so the workspace can populate the tree. */
  readonly onNodesChange?: (nodes: readonly SpaceSceneNode[]) => void;
  /** Fired when the camera turns, with where the world axes now point on screen. */
  readonly onOrientation?: (axes: SpaceAxesOrientation) => void;
  readonly activeTool?: SpaceTool;
  /** A CSS colour for the canvas background. */
  readonly backgroundColor?: string;
  /** Overlay each material with wireframe rendering. Diagnostic aid. */
  readonly wireframe?: boolean;
  /** Draw X/Y/Z axes in the scene, from the model's floor centre. */
  readonly showAxes?: boolean;
  /** True when the model is in metres (LiDAR depth reached the solver). */
  readonly metric?: boolean;
  /** Increment to fit the model in view, keeping the current angle. */
  readonly fitSignal?: number;
  /** Increment to return to the starting angle and fit. */
  readonly resetSignal?: number;
}

type Phase =
  | { readonly kind: "loading" }
  | { readonly kind: "ready" }
  | { readonly kind: "unavailable" }
  | { readonly kind: "error"; readonly message: string };

const START_THETA = Math.PI * 0.25;
const START_PHI = Math.PI * 0.3;

export function SpaceViewer({
  modelKey,
  loadModel,
  hiddenUuids,
  selectedUuid = null,
  onSelectMesh,
  onNodesChange,
  onOrientation,
  activeTool = "select",
  backgroundColor = "#0d141c",
  wireframe = false,
  showAxes = true,
  metric = false,
  fitSignal = 0,
  resetSignal = 0,
}: SpaceViewerProps): ReactNode {
  const mountRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });

  // Callbacks and tool state read inside long-lived listeners, kept current
  // without re-running the mount effect (which would reload the model).
  const live = useRef({ onSelectMesh, onNodesChange, onOrientation, activeTool, metric });
  live.current = { onSelectMesh, onNodesChange, onOrientation, activeTool, metric };

  const controlsRef = useRef<{
    setHidden(uuids: ReadonlySet<string>): void;
    setSelected(uuid: string | null): void;
    setBackground(colour: string): void;
    setWireframe(on: boolean): void;
    setAxes(on: boolean): void;
    fit(): void;
    reset(): void;
    setMeasureActive(on: boolean): void;
  } | null>(null);

  // The first values, for the effect below; later changes go through the
  // handle so the model is not reloaded on a colour tweak.
  const initial = useRef({ hiddenUuids, selectedUuid, backgroundColor, wireframe, showAxes });

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    setPhase({ kind: "loading" });

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(initial.current.backgroundColor);
    const camera = new THREE.PerspectiveCamera(50, 1, 0.02, 2000);

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    } catch {
      setPhase({ kind: "unavailable" });
      return;
    }
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    const canvas = renderer.domElement;
    canvas.style.display = "block";
    canvas.style.touchAction = "none";
    mount.appendChild(canvas);

    // Only lit materials notice these (the Space GLB is unlit); kept so a
    // lit model from another pipeline still has shape.
    scene.add(new THREE.HemisphereLight(0xffffff, 0x606060, 1.0));

    let cancelled = false;
    let loaded: THREE.Object3D | null = null;
    let radius = 5;
    let theta = START_THETA;
    let phi = START_PHI;
    const centre = new THREE.Vector3();
    let needsRender = true;
    let axesHelper: THREE.AxesHelper | null = null;
    let selectionBox: THREE.Box3Helper | null = null;

    // Measure tool: two clicks make a line; a third starts again.
    let measureActive = live.current.activeTool === "measure";
    let measureFirst: THREE.Vector3 | null = null;
    let measureLine: THREE.Line | null = null;
    let measureLabelEl: HTMLDivElement | null = null;
    let measureLabelWorld: THREE.Vector3 | null = null;

    const raycaster = new THREE.Raycaster();
    const pointerNdc = new THREE.Vector2();

    const clearMeasure = (): void => {
      measureFirst = null;
      if (measureLine) {
        scene.remove(measureLine);
        measureLine.geometry.dispose();
        (measureLine.material as THREE.Material).dispose();
        measureLine = null;
      }
      measureLabelEl?.remove();
      measureLabelEl = null;
      measureLabelWorld = null;
      needsRender = true;
    };

    const drawMeasure = (a: THREE.Vector3, b: THREE.Vector3): void => {
      const geometry = new THREE.BufferGeometry().setFromPoints([a, b]);
      const material = new THREE.LineBasicMaterial({ color: 0xff5a00, depthTest: false });
      measureLine = new THREE.Line(geometry, material);
      measureLine.renderOrder = 999;
      scene.add(measureLine);
      const metres = a.distanceTo(b);
      const label = document.createElement("div");
      label.className = "space-workspace__measure-label";
      label.textContent = metres >= 1 ? `${metres.toFixed(2)} m` : `${Math.round(metres * 1000)} mm`;
      mount.appendChild(label);
      measureLabelEl = label;
      measureLabelWorld = a.clone().lerp(b, 0.5);
      needsRender = true;
    };

    const updateMeasureLabel = (): void => {
      if (!measureLabelEl || !measureLabelWorld) return;
      const p = measureLabelWorld.clone().project(camera);
      const rect = mount.getBoundingClientRect();
      measureLabelEl.style.transform = `translate(${(p.x * 0.5 + 0.5) * rect.width}px, ${(-p.y * 0.5 + 0.5) * rect.height}px) translate(-50%, -120%)`;
      measureLabelEl.style.opacity = p.z > 1 ? "0" : "1";
    };

    const publishOrientation = (): void => {
      const report = live.current.onOrientation;
      if (!report) return;
      const inverse = camera.quaternion.clone().invert();
      const axis = (x: number, y: number, z: number) => {
        const v = new THREE.Vector3(x, y, z).applyQuaternion(inverse);
        return [v.x, -v.y, v.z] as const;
      };
      report({ x: axis(1, 0, 0), y: axis(0, 1, 0), z: axis(0, 0, 1) });
    };

    const place = (): void => {
      camera.position.set(
        centre.x + radius * Math.sin(phi) * Math.sin(theta),
        centre.y + radius * Math.cos(phi),
        centre.z + radius * Math.sin(phi) * Math.cos(theta),
      );
      camera.lookAt(centre);
      camera.updateMatrixWorld();
      needsRender = true;
      publishOrientation();
    };

    const fit = (): void => {
      if (!loaded) return;
      const sphere = new THREE.Box3().setFromObject(loaded).getBoundingSphere(new THREE.Sphere());
      centre.copy(sphere.center);
      const fov = THREE.MathUtils.degToRad(camera.fov);
      const fitHeight = sphere.radius / Math.sin(fov / 2);
      const fitWidth = sphere.radius / Math.sin(Math.atan(Math.tan(fov / 2) * camera.aspect));
      radius = Math.max(fitHeight, fitWidth) * 0.9;
      camera.near = Math.max(0.01, sphere.radius / 500);
      camera.far = sphere.radius * 50;
      camera.updateProjectionMatrix();
      place();
    };

    const reset = (): void => {
      theta = START_THETA;
      phi = START_PHI;
      fit();
    };

    // ------------------------------------------------------------ pointer
    let drag: { mode: "orbit" | "pan"; x: number; y: number; moved: boolean } | null = null;
    const onDown = (event: PointerEvent): void => {
      const pan =
        event.button === 2 || event.shiftKey || event.ctrlKey || live.current.activeTool === "pan";
      drag = { mode: pan ? "pan" : "orbit", x: event.clientX, y: event.clientY, moved: false };
      canvas.setPointerCapture?.(event.pointerId);
    };
    const onMove = (event: PointerEvent): void => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 2) drag.moved = true;
      drag.x = event.clientX;
      drag.y = event.clientY;
      if (drag.mode === "orbit") {
        theta -= dx * 0.008;
        phi = Math.min(Math.PI - 0.05, Math.max(0.05, phi - dy * 0.008));
      } else {
        // Move the orbit centre in the camera's own plane, scaled so the
        // model tracks the pointer at the centre's depth.
        const perPixel =
          (2 * radius * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2)) /
          Math.max(1, mount.clientHeight);
        const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
        const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
        centre.addScaledVector(right, -dx * perPixel).addScaledVector(up, dy * perPixel);
      }
      place();
    };
    const pickAt = (clientX: number, clientY: number): THREE.Intersection | null => {
      if (!loaded) return null;
      const rect = mount.getBoundingClientRect();
      pointerNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      pointerNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointerNdc, camera);
      return raycaster.intersectObject(loaded, true)[0] ?? null;
    };
    const topLevelAncestor = (object: THREE.Object3D): THREE.Object3D => {
      let current = object;
      while (current.parent && current.parent !== loaded && current.parent !== scene) {
        current = current.parent;
      }
      return current;
    };
    const onUp = (event: PointerEvent): void => {
      const wasClick = drag !== null && !drag.moved && drag.mode === "orbit";
      drag = null;
      if (!wasClick || event.button !== 0) return;
      const hit = pickAt(event.clientX, event.clientY);
      if (measureActive) {
        if (!hit) return;
        if (!measureFirst) {
          clearMeasure();
          measureFirst = hit.point.clone();
        } else {
          drawMeasure(measureFirst, hit.point.clone());
          measureFirst = null;
        }
        return;
      }
      live.current.onSelectMesh?.(hit ? topLevelAncestor(hit.object).uuid : null);
    };
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      radius = Math.max(0.05, radius * Math.exp(event.deltaY * 0.0015));
      place();
    };
    const onContextMenu = (event: MouseEvent): void => event.preventDefault();

    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("contextmenu", onContextMenu);

    const resize = (): void => {
      const { clientWidth, clientHeight } = mount;
      if (clientWidth === 0 || clientHeight === 0) return;
      renderer.setSize(clientWidth, clientHeight, false);
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
      needsRender = true;
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(mount);

    // ---------------------------------------------------- workspace handles
    const applyHidden = (uuids: ReadonlySet<string>): void => {
      if (!loaded) return;
      for (const child of loaded.children) child.visible = !uuids.has(child.uuid);
      needsRender = true;
    };
    const applySelection = (uuid: string | null): void => {
      if (selectionBox) {
        scene.remove(selectionBox);
        selectionBox.geometry.dispose();
        (selectionBox.material as THREE.Material).dispose();
        selectionBox = null;
      }
      needsRender = true;
      if (!loaded || !uuid) return;
      const target = loaded.children.find((child) => child.uuid === uuid);
      if (!target) return;
      const box = new THREE.Box3().setFromObject(target);
      if (box.isEmpty()) return;
      selectionBox = new THREE.Box3Helper(box, new THREE.Color(0x4fb3ff));
      (selectionBox.material as THREE.LineBasicMaterial).depthTest = false;
      selectionBox.renderOrder = 998;
      scene.add(selectionBox);
    };
    const applyWireframe = (on: boolean): void => {
      loaded?.traverse((object) => {
        const material = (object as THREE.Mesh).material as
          | (THREE.Material & { wireframe?: boolean })
          | Array<THREE.Material & { wireframe?: boolean }>
          | undefined;
        if (!material) return;
        for (const m of Array.isArray(material) ? material : [material]) m.wireframe = on;
      });
      needsRender = true;
    };
    const applyAxes = (on: boolean): void => {
      if (axesHelper) {
        scene.remove(axesHelper);
        axesHelper.dispose();
        axesHelper = null;
      }
      needsRender = true;
      if (!on || !loaded) return;
      // From the centre of the floor: a metre long on a metric model (a
      // physical ruler for the eye), a fifth of the model otherwise.
      const box = new THREE.Box3().setFromObject(loaded);
      const size = box.getSize(new THREE.Vector3());
      const length = live.current.metric ? 1 : Math.max(size.x, size.y, size.z) * 0.2;
      axesHelper = new THREE.AxesHelper(length);
      const floorCentre = box.getCenter(new THREE.Vector3());
      floorCentre.y = box.min.y;
      axesHelper.position.copy(floorCentre);
      (axesHelper.material as THREE.LineBasicMaterial).depthTest = false;
      axesHelper.renderOrder = 997;
      scene.add(axesHelper);
    };

    controlsRef.current = {
      setHidden: applyHidden,
      setSelected: applySelection,
      setBackground: (colour) => {
        scene.background = new THREE.Color(colour);
        needsRender = true;
      },
      setWireframe: applyWireframe,
      setAxes: applyAxes,
      fit,
      reset,
      setMeasureActive: (on) => {
        measureActive = on;
        if (!on) clearMeasure();
      },
    };

    let frame = requestAnimationFrame(function draw(): void {
      frame = requestAnimationFrame(draw);
      if (!needsRender) return;
      needsRender = false;
      updateMeasureLabel();
      renderer.render(scene, camera);
    });

    loadModel()
      .then(
        (bytes) =>
          new Promise<THREE.Object3D>((resolve, reject) => {
            new GLTFLoader().parse(bytes, "", (gltf) => resolve(gltf.scene), reject);
          }),
      )
      .then((root) => {
        if (cancelled) {
          disposeTree(root);
          return;
        }
        loaded = root;
        scene.add(root);
        live.current.onNodesChange?.(
          root.children.map((child, index) => ({
            uuid: child.uuid,
            name: child.name || `Mesh ${index + 1}`,
            triangles: countTriangles(child),
          })),
        );
        reset();
        applyHidden(initial.current.hiddenUuids ?? new Set());
        applySelection(initial.current.selectedUuid ?? null);
        applyWireframe(initial.current.wireframe);
        applyAxes(initial.current.showAxes);
        setPhase({ kind: "ready" });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPhase({
          kind: "error",
          message: error instanceof Error ? error.message : "The model file could not be loaded.",
        });
      });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("contextmenu", onContextMenu);
      clearMeasure();
      if (selectionBox) {
        selectionBox.geometry.dispose();
        (selectionBox.material as THREE.Material).dispose();
      }
      axesHelper?.dispose();
      if (loaded) disposeTree(loaded);
      renderer.dispose();
      canvas.remove();
      controlsRef.current = null;
    };
    // Reload only when the model changes; everything else goes through the
    // handle below, so a colour tweak does not cost the reader their view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelKey]);

  useEffect(() => {
    controlsRef.current?.setHidden(hiddenUuids ?? new Set());
  }, [hiddenUuids]);
  useEffect(() => {
    controlsRef.current?.setSelected(selectedUuid);
  }, [selectedUuid]);
  useEffect(() => {
    controlsRef.current?.setBackground(backgroundColor);
  }, [backgroundColor]);
  useEffect(() => {
    controlsRef.current?.setWireframe(wireframe);
  }, [wireframe]);
  useEffect(() => {
    controlsRef.current?.setAxes(showAxes);
  }, [showAxes]);
  useEffect(() => {
    controlsRef.current?.setMeasureActive(activeTool === "measure");
  }, [activeTool]);
  useEffect(() => {
    if (fitSignal > 0) controlsRef.current?.fit();
  }, [fitSignal]);
  useEffect(() => {
    if (resetSignal > 0) controlsRef.current?.reset();
  }, [resetSignal]);

  if (phase.kind === "unavailable") {
    return (
      <div data-testid="space-viewer" role="note" className="space-viewer__note">
        <p>
          The textured 3D model cannot be shown because this browser has no
          WebGL. The floor plan and measurements do not need it.
        </p>
      </div>
    );
  }

  if (phase.kind === "error") {
    return (
      <div data-testid="space-viewer" className="space-viewer__note" role="alert">
        <p>
          The model is ready, but its file could not be loaded.{" "}
          <span className="small">{phase.message}</span>
        </p>
      </div>
    );
  }

  return (
    <div
      ref={mountRef}
      data-testid="space-viewer"
      data-phase={phase.kind}
      aria-label="Textured 3D space model"
      className="space-viewer"
      style={{
        cursor:
          activeTool === "measure" ? "crosshair" : activeTool === "pan" ? "move" : "grab",
      }}
    >
      {phase.kind === "loading" ? (
        <p className="space-viewer__loading" role="status" aria-live="polite">
          Loading the textured model…
        </p>
      ) : null}
    </div>
  );
}

function countTriangles(object: THREE.Object3D): number {
  let total = 0;
  object.traverse((child) => {
    const geometry = (child as THREE.Mesh).geometry;
    if (!geometry) return;
    if (geometry.index) total += geometry.index.count / 3;
    else if (geometry.attributes["position"]) total += geometry.attributes["position"].count / 3;
  });
  return Math.round(total);
}

/** Frees the GPU resources three allocates for a loaded scene. */
function disposeTree(root: THREE.Object3D): void {
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    mesh.geometry?.dispose();
    const material = mesh.material;
    for (const m of Array.isArray(material) ? material : material ? [material] : []) {
      for (const value of Object.values(m as unknown as Record<string, unknown>)) {
        if (value instanceof THREE.Texture) value.dispose();
      }
      m.dispose();
    }
  });
}
